import axios from 'axios';
import { ErrorDto } from 'services/dtos/errors-dto';
import { useErrorHandler } from 'tools/error-handler';
import {
    CreateVisitorPassInput,
    CreateVisitorPassResponse,
    VisitorPassConfirmResponse,
    VisitorPassEventMethod,
    VisitorPasses,
    VisitorPassVerifyResponse,
} from 'services/visitor-passes/visitor-passes.service';

export const useAPIVisitorPasses = (): VisitorPasses => {
    const errorHandler = useErrorHandler({ navigateOnNetworkError: false });

    const createVisitorPass = async (
        accessToken: string,
        input: CreateVisitorPassInput,
    ): Promise<CreateVisitorPassResponse> => {
        try {
            const response = await axios.post(
                process.env.REACT_APP_API_MOBILE_PASSCODES_BASE_URL + `/user/visitor-passes`,
                input,
                { headers: { Authorization: 'Bearer ' + accessToken } },
            );
            return { ...response.data, id: response.data.id.toLowerCase() } as CreateVisitorPassResponse;
        } catch (err: unknown) {
            errorHandler(err);
            const error = err as ErrorDto;
            throw { msg: error.response?.data?.msg, isNetworkError: (err as { code?: string }).code === 'ERR_NETWORK' };
        }
    };

    const verifyVisitorPass = async (accessToken: string, token: string): Promise<VisitorPassVerifyResponse> => {
        try {
            const response = await axios.post(
                process.env.REACT_APP_API_MOBILE_PASSCODES_BASE_URL + `/user/visitor-passes/verify`,
                { token },
                { headers: { Authorization: 'Bearer ' + accessToken } },
            );
            return response.data as VisitorPassVerifyResponse;
        } catch (err: unknown) {
            errorHandler(err);
            const error = err as ErrorDto;
            throw { msg: error.response?.data?.msg, isNetworkError: (err as { code?: string }).code === 'ERR_NETWORK' };
        }
    };

    const confirmVisitorPass = async (
        accessToken: string,
        passId: string,
        method: VisitorPassEventMethod,
    ): Promise<VisitorPassConfirmResponse> => {
        try {
            const response = await axios.post(
                process.env.REACT_APP_API_MOBILE_PASSCODES_BASE_URL + `/user/visitor-passes/${passId}/confirm`,
                { method },
                { headers: { Authorization: 'Bearer ' + accessToken } },
            );
            return response.data as VisitorPassConfirmResponse;
        } catch (err: unknown) {
            errorHandler(err);
            const error = err as ErrorDto;
            // A diferencia de create/verify, acá se preserva error_id: el guardia
            // necesita distinguir "ya se usó"/"revocado"/"vencido" del error de red
            // genérico (ver reception.controller.ts:onConfirm).
            throw {
                msg: error.response?.data?.msg,
                error_id: error.response?.data?.error_id,
                isNetworkError: (err as { code?: string }).code === 'ERR_NETWORK',
            };
        }
    };

    return { createVisitorPass, verifyVisitorPass, confirmVisitorPass };
};
